import { Link, useParams, Navigate } from "react-router-dom";
import { MessageCircle, Phone, MapPin, Check, ArrowRight, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { JsonLd } from "@/components/JsonLd";
import { CONTACT, SITE_URL } from "@/content";
import { useSettings } from "@/hooks/useSettings";
import { landingData } from "@/pages/landing/landingData";

export default function LandingPage({ slug: propSlug }) {
  const params = useParams();
  const slug = propSlug || params.slug;
  const data = landingData[slug];
  const { hours } = useSettings();

  if (!data) return <Navigate to="/" replace />;

  const url = `${SITE_URL}/${slug}`;
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Startseite", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: data.crumbLabel, item: url },
    ],
  };
  const faqSchema = data.faqs && {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div data-testid={`landing-${slug}`}>
      <Seo title={data.metaTitle} description={data.metaDescription} path={`/${slug}`} keywords={data.keywords} />
      <JsonLd id={`bc-${slug}`} data={breadcrumbSchema} />
      {faqSchema && <JsonLd id={`faq-${slug}`} data={faqSchema} />}

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0">
          <img src={data.heroImage} alt={data.heroAlt} className="h-full w-full object-cover opacity-40" loading="eager" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, hsl(20 14% 4.5% / 0.95) 0%, hsl(20 14% 4.5% / 0.85) 45%, hsl(18 30% 8% / 0.7) 100%)" }} />
          <div className="hero-glow absolute inset-0" />
          <div className="bg-grid absolute inset-0" />
        </div>
        <div aria-hidden="true" className="pointer-events-none absolute -right-6 top-0 select-none font-jp text-[140px] font-black leading-none text-white/[0.05] md:text-[220px]">
          {data.kanji}
        </div>
        <div className="relative z-10 mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-20">
          {/* Breadcrumb */}
          <nav data-testid="breadcrumb" aria-label="Brotkrümel" className="flex items-center gap-1.5 font-mono-ui text-[11px] uppercase tracking-wider text-muted-foreground">
            <Link to="/" className="transition-colors hover:text-foreground">Start</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-primary">{data.crumbLabel}</span>
          </nav>
          <Reveal>
            <Badge variant="outline" className="mt-6 rounded-full border-primary/40 bg-primary/10 px-4 py-1.5 font-mono-ui text-[11px] uppercase tracking-widest text-primary backdrop-blur-sm">
              {data.eyebrow} · Köln
            </Badge>
            <h1 className="mt-5 max-w-3xl font-display text-5xl uppercase leading-[0.95] tracking-[0.04em] sm:text-6xl">
              {data.h1}
            </h1>
            {data.intro.map((p, i) => (
              <p key={i} className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">{p}</p>
            ))}
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" data-testid="landing-phone-button" className="h-12 rounded-xl bg-primary px-6 font-medium text-primary-foreground shadow-[0_10px_30px_hsl(22_100%_50%/0.35)] transition-shadow hover:shadow-[0_14px_44px_hsl(22_100%_50%/0.5)]">
                <a href={CONTACT.phoneHref}><Phone className="mr-2 h-5 w-5" /> Jetzt anrufen</a>
              </Button>
              <Button asChild size="lg" variant="outline" data-testid="landing-whatsapp-button" className="h-12 rounded-xl border-primary/30 bg-background/40 px-6 backdrop-blur-sm hover:bg-primary/10">
                <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer"><MessageCircle className="mr-2 h-5 w-5" /> WhatsApp</a>
              </Button>
              <a href={CONTACT.mapsHref} target="_blank" rel="noopener noreferrer" className="link-slide flex items-center gap-1.5 px-2 py-3 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
                <MapPin className="h-4 w-4 text-primary" /> Route planen
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ SECTIONS ============ */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
        <div className="space-y-12">
          {data.sections.map((sec, i) => (
            <Reveal key={sec.heading} delay={i * 0.05}>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:items-start">
                <div className="md:col-span-5">
                  <span aria-hidden="true" className="neon-kanji-orange text-sm">{sec.kanji}</span>
                  <h2 className="mt-2 font-display text-3xl uppercase tracking-[0.04em] sm:text-4xl">{sec.heading}</h2>
                </div>
                <div className="md:col-span-7">
                  {sec.body.map((p, j) => (
                    <p key={j} className="text-sm leading-relaxed text-muted-foreground md:text-base">{p}</p>
                  ))}
                  {sec.bullets && (
                    <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {sec.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2.5 text-sm">
                          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15">
                            <Check className="h-3 w-3 text-primary" />
                          </span>
                          <span className="text-muted-foreground">{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ FAQ ============ */}
      {data.faqs && (
        <section className="border-y border-border bg-card/50">
          <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 md:py-24">
            <Reveal>
              <div className="flex items-center gap-3">
                <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-primary">Häufige Fragen</p>
                <span aria-hidden="true" className="neon-kanji-orange text-sm">質問</span>
              </div>
              <h2 className="mt-3 font-display text-4xl uppercase tracking-[0.04em] sm:text-5xl">
                {data.crumbLabel.replace(" Köln", "")} – <span className="neon-text">deine Fragen.</span>
              </h2>
            </Reveal>
            <div data-testid="landing-faq" className="mt-10 space-y-4">
              {data.faqs.map((faq, i) => (
                <Reveal key={faq.q} delay={i * 0.05}>
                  <details data-testid={`landing-faq-${i + 1}`} className="group neon-card rounded-2xl border border-border bg-card p-5 md:p-6">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
                      <h3 className="font-display text-lg uppercase tracking-wide text-foreground md:text-xl">{faq.q}</h3>
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-primary/40 text-primary transition-transform group-open:rotate-45">+</span>
                    </summary>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground md:text-base">{faq.a}</p>
                  </details>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ RELATED + CTA ============ */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
        <Reveal>
          <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-primary">Das könnte dich auch interessieren</p>
          <div className="mt-5 flex flex-wrap gap-3">
            {data.related.map((r) => (
              <Link key={r.to} to={r.to} data-testid={`related-${r.to}`} className="group inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground">
                {r.label}
                <ArrowRight className="h-3.5 w-3.5 text-primary transition-transform group-hover:translate-x-0.5" />
              </Link>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="noise relative mt-12 overflow-hidden rounded-2xl border border-border bg-card p-8 text-center md:p-12">
            <div className="cta-glow pointer-events-none absolute inset-0" />
            <div className="relative z-10">
              <h2 className="font-display text-3xl uppercase tracking-[0.04em] sm:text-4xl">
                Besuch uns <span className="neon-text">im Store.</span>
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground md:text-base">
                {CONTACT.addressStreet}, {CONTACT.addressCity}. Komm während unserer Öffnungszeiten vorbei –
                {" "}{hours.map((h) => `${h.days}: ${h.time}`).slice(0, 2).join(" · ")}.
              </p>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <Button asChild size="lg" className="h-12 rounded-xl bg-primary px-6 font-medium text-primary-foreground">
                  <a href={CONTACT.phoneHref}><Phone className="mr-2 h-5 w-5" /> {CONTACT.phoneDisplay}</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="h-12 rounded-xl border-primary/30 bg-transparent px-6 hover:bg-primary/10">
                  <Link to="/anfahrt"><MapPin className="mr-2 h-5 w-5" /> Anfahrt & Öffnungszeiten</Link>
                </Button>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
